import useDefaultToast from '@/components/useDefaultToast'
import {
  AIGeneratedForm,
  createPreviewFormThunk,
  selectGeneratedFormDataByConversationId,
} from '@/redux/slices/formAIGenerationSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useRouter } from 'expo-router'
import { RefObject, useImperativeHandle, useRef } from 'react'
import { buildFormAttributes, buildFormThemeAttributes } from './buildFormAttributes'

export type AIGeneratedFormPreviewRef = {
  preview: () => void
}

type AIGeneratedFormPreviewProps = {
  ref?: RefObject<AIGeneratedFormPreviewRef | null>
  conversationId: string
  generatedForm: AIGeneratedForm
  children?: (preview: () => void) => React.ReactNode
}

export default function AIGeneratedFormPreview({
  ref,
  conversationId,
  generatedForm,
  children,
}: AIGeneratedFormPreviewProps) {
  const toast = useDefaultToast()
  const router = useRouter()
  const dispatch = useAppDispatch()
  const generatedFormsData = useAppSelector(state => selectGeneratedFormDataByConversationId(state, conversationId))

  const isPreviewLoadingRef = useRef(false)

  const handlePreview = async () => {
    if (isPreviewLoadingRef.current) return

    isPreviewLoadingRef.current = true
    const previewResultsPromise = Promise.all(
      generatedFormsData.map(item => {
        const cachePayload = JSON.stringify(item.form)
        if (
          !item.previewStates ||
          item.previewStates.expiresAt < Date.now() ||
          JSON.stringify(item.form) !== item.previewStates.cachePayload
        ) {
          return (item.form.theme ? buildFormThemeAttributes(item.form.theme) : Promise.resolve(undefined)).then(
            themeAttributes =>
              dispatch(
                createPreviewFormThunk({
                  generatedFormId: item.form.id,
                  scene: item.form.scene,
                  formAttributes: buildFormAttributes(item.form, item.backendMessageId).attributes,
                  themeAttributes,
                  cachePayload,
                })
              )
                .unwrap()
                .then(cacheToken => ({ form: item.form, cacheToken, backendMessageId: item.backendMessageId }))
          )
        }
        return { form: item.form, cacheToken: item.previewStates.cacheToken, backendMessageId: item.backendMessageId }
      })
    )

    let previewResults: Awaited<typeof previewResultsPromise>
    try {
      previewResults = await previewResultsPromise
    } catch (error) {
      console.error('failed to create form preview', error)
      toast.show('预览失败')
      return
    } finally {
      isPreviewLoadingRef.current = false
    }

    const currentCacheToken = previewResults.find(item => item.form.id === generatedForm.id)?.cacheToken
    if (!currentCacheToken) return

    const url = `${process.env.EXPO_PUBLIC_NEXT_BASE_URL}/forms/new/preview/${currentCacheToken}?versions=${encodeURIComponent(
      previewResults.map(v => `${v.cacheToken}:${v.backendMessageId || ''}:${v.form.scene || ''}`).join(',')
    )}`

    router.push(`/forms/ai_create/preview?url=${encodeURIComponent(url)}`)
  }

  useImperativeHandle(ref, () => ({ preview: handlePreview }))

  return children?.(handlePreview) ?? null
}
